import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Image,
	ImageBackground,
	ImageSourcePropType,
	Linking,
	StyleSheet,
	Text,
	TextInput,
	View,
} from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';
import { useDispatch, useSelector } from 'react-redux';

import { BgLandscapePng, BgPortraitPng } from '../../../../assets';
import AppleIcon from '../../../../assets/login/welcome/apple.svg';
import CheckIcon from '../../../../assets/login/welcome/check.svg';
import ChevronIcon from '../../../../assets/login/welcome/chevron.svg';
import CloseIcon from '../../../../assets/login/welcome/close.svg';
import EmailIcon from '../../../../assets/login/welcome/email.svg';
import FacebookIcon from '../../../../assets/login/welcome/facebook.svg';
import FlagEn from '../../../../assets/login/welcome/flag-en.svg';
import FlagTr from '../../../../assets/login/welcome/flag-tr.svg';
import GoogleIcon from '../../../../assets/login/welcome/google.svg';
import LogoBgImage from '../../../../assets/login/welcome/logo-bg.png';
import LogoEnImage from '../../../../assets/login/welcome/logo-en.png';
import LogoFrImage from '../../../../assets/login/welcome/logo-fr.png';
import LogoRuImage from '../../../../assets/login/welcome/logo-ru.png';
import LogoTrImage from '../../../../assets/login/welcome/logo-tr.png';
import { testProps } from '../../../../config';
import { renderGoogleSignInButton } from '../../../../services/socialLogins/googleSignIn.web';
import { shouldUseSocialRedirect } from '../../../../services/socialLogins/socialRedirect.web';
import { RootState } from '../../../../store';
import {
	appleLoginRequest,
	facebookLoginRequest,
	googleLoginRequest,
	loginRequest,
	registerRequest,
} from '../../../../store/auth/authActions';
import { i18nLoadRequest } from '../../../../store/i18n/i18nActions';
import { env } from '../../../../utils/env';
import { useDevice } from '../../../../utils/hooks/useDevice';
import { useSafeViewport } from '../../../../utils/hooks/useSafeViewport';
import { useSvgId } from '../../../../utils/hooks/useSvgId';
import GradientPlate, { GradientStops } from '../../common/GradientPlate';
import { IndicatorScrollView } from '../../common/ScrollIndicator';
import { Pressable, TouchableOpacity } from '../../common/Touchable';
import InviteCodeField from './InviteCodeField';

/**
 * Figma "Pre-login / Welcome POLISH Final" (5947:77482) and its open language menu (5948:29510):
 * the whole-screen log in. Everything is laid out in the 390 x 844 design frame and scaled to the
 * screen's width; a frame taller than the screen scrolls. "Continue with Email" swaps the four
 * sign-in buttons for the username / password form in the same place.
 */

const DESIGN = { width: 390, height: 844 } as const;
const HERO = { x: -25, y: 64, width: 440, height: 360.3 } as const;
const CLOSE = { x: 342, y: 44, size: 32, icon: 24 } as const;
const STACK = { x: 24, y: 419, width: 342, buttonHeight: 48, gap: 12, iconX: 69 } as const;
/** "Bottom Fade" (5947:77495): from y 249 to the bottom, clear to #0F0000 via 85% at 18%. */
const FADE_TOP = 249;
const STACK_TO_LANGUAGE = 32;
const LANGUAGE_TO_LEGAL = 32;
const LANGUAGE = { height: 36, menuWidth: 232, menuHeight: 242, menuGap: 8 } as const;

const LOGIN_FILL: GradientStops = [
	['0', '#66D93D'],
	['0.55', '#248F1C'],
	['1', '#0D540F'],
];
const EMAIL_FILL: GradientStops = [
	['0', '#970501'],
	['1', '#220802'],
];

const RECAPTCHA_PRIVACY_URL = 'https://policies.google.com/privacy';
const RECAPTCHA_TERMS_URL = 'https://policies.google.com/terms';

type LanguageCode = 'bg' | 'en' | 'tr' | 'fr' | 'ru';

const LOGO_IMAGES: Record<LanguageCode, ImageSourcePropType> = {
	bg: LogoBgImage,
	en: LogoEnImage,
	fr: LogoFrImage,
	ru: LogoRuImage,
	tr: LogoTrImage,
};

/** The menu's order in the file, with the ids the i18n loader expects. */
const LANGUAGES: Array<{ code: LanguageCode; id: number; label: string }> = [
	{ code: 'bg', id: 2, label: 'Български' },
	{ code: 'en', id: 1, label: 'English' },
	{ code: 'tr', id: 5, label: 'Türkçe' },
	{ code: 'fr', id: 3, label: 'Français' },
	{ code: 'ru', id: 4, label: 'Русский' },
];

type LoginData = { username: string; password: string };
type LoginErrors = { username?: string; password?: string };

type Props = {
	data: LoginData;
	errors: LoginErrors;
	onChange: (name: keyof LoginData, value: string) => void;
	setErrors: (errors: LoginErrors) => void;
	onClose?: () => void;
	disabled?: boolean;
};

const LoginWelcome = ({ data, errors, onChange, setErrors, onClose, disabled = false }: Props) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { isDesktop, isWeb, isIOS, isLandscape } = useDevice();
	const { width, height } = useSafeViewport();
	const langCode = useSelector((state: RootState) => state.i18n?.langCode ?? 'en') as LanguageCode;
	const logoImage = LOGO_IMAGES[langCode];
	const authLoading = useSelector((state: RootState) => state.auth.loading);
	const busy = disabled || Boolean(authLoading);

	const [step, setStep] = useState<'welcome' | 'email'>('welcome');
	const [isRegistering, setIsRegistering] = useState(false);
	const [languageOpen, setLanguageOpen] = useState(false);
	const [languageTop, setLanguageTop] = useState<number | null>(null);
	const passwordRef = useRef<TextInput>(null);
	const submitRef = useRef<View>(null);
	const googleButtonId = `google-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
	const isDev = env('DEV_MODE', 'false') === 'true';

	// Scale to the width; only desktop landscape, where the frame stands as a column, is also
	// fitted to the height.
	const isDesktopLandscape = isDesktop && isLandscape;
	const s = Math.min(width / DESIGN.width, isDesktopLandscape ? height / DESIGN.height : 1.25);
	const k = (value: number) => value * s;
	const frameWidth = k(DESIGN.width);
	const frameLeft = (width - frameWidth) / 2;
	const styles = createStyles(k, frameLeft);

	const showApple = isIOS || isWeb;
	const stackWidth = k(STACK.width);

	// Google's web sign-in has to be Google's own button. It is drawn at the design button's size
	// and laid, all but invisible, over it - the design is what shows, Google's is what is clicked.
	// The installed web app is the exception: Google's button answers through a popup, which an
	// installed app cannot receive, so there the design button itself starts the redirect login.
	const useGoogleWebButton = isWeb && !shouldUseSocialRedirect();
	useEffect(() => {
		if (!useGoogleWebButton || busy || step !== 'welcome') return;
		const container = document.getElementById(googleButtonId);
		if (!container) return;
		let active = true;
		renderGoogleSignInButton(
			container,
			credential => {
				if (active) dispatch(googleLoginRequest({ idToken: credential }));
			},
			{ variant: 'wide', width: stackWidth },
		).catch(error =>
			console.error('[GoogleSignIn:web] Failed to render Google button:', error?.message || error),
		);
		return () => {
			active = false;
			container.replaceChildren();
		};
	}, [busy, dispatch, googleButtonId, useGoogleWebButton, stackWidth, step]);

	const submit = () => {
		const identifier = data.username?.trim();
		const password = data.password || '';
		const nextErrors: LoginErrors = {};
		if (!identifier) {
			nextErrors.username = isRegistering
				? t('login.register_error_email_required')
				: t('T_SITE_LOGIN_ERROR_USERNAME');
		}
		if (!password) nextErrors.password = t('T_SITE_LOGIN_ERROR_PASSWORD');
		if (isRegistering && identifier && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier)) {
			nextErrors.username = t('login.register_error_email_invalid');
		}
		if (Object.keys(nextErrors).length > 0) {
			setErrors(nextErrors);
			return;
		}
		setErrors({});
		dispatch(
			isRegistering
				? registerRequest({ email: identifier, password, username: identifier, ageConfirmed: true })
				: loginRequest({ username: identifier, password }),
		);
	};

	const changeLanguage = (code: LanguageCode) => {
		setLanguageOpen(false);
		const language = LANGUAGES.find(item => item.code === code);
		if (!language || language.code === langCode) return;
		dispatch(i18nLoadRequest({ langId: language.id, langCode: language.code }));
	};

	const currentLanguage = LANGUAGES.find(item => item.code === langCode) ?? LANGUAGES[1];
	const contentHeight = Math.max(height, k(DESIGN.height));

	return (
		<ImageBackground
			source={isDesktopLandscape ? BgLandscapePng : BgPortraitPng}
			resizeMode='cover'
			style={styles.root}
		>
			<IndicatorScrollView
				style={styles.fill}
				contentContainerStyle={{ minHeight: contentHeight }}
				keyboardShouldPersistTaps='handled'
				// The form sits in the lower half the keyboard covers: keep the focused field, and the
				// LOG IN button under it, in sight above the keyboard.
				avoidKeyboard={{ submitRef }}
				reserveGutter={false}
				indicatorInsets={{ right: 4 }}
			>
				<BottomFade top={k(FADE_TOP)} width={width} height={contentHeight - k(FADE_TOP)} />

				<Image source={logoImage} style={styles.hero} resizeMode='contain' />

				{onClose ? (
					<TouchableOpacity
						accessibilityRole='button'
						accessibilityLabel={t('common.close', 'Close')}
						disabled={busy}
						onPress={onClose}
						hitSlop={8}
						style={styles.close}
					>
						<CloseIcon width={k(CLOSE.icon)} height={k(CLOSE.icon)} />
					</TouchableOpacity>
				) : null}

				<View style={styles.lower}>
					{step === 'welcome' ? (
						<View style={styles.stack}>
							<SignInButton
								k={k}
								label={t('login.continue_with_facebook', 'Continue with Facebook')}
								icon={<FacebookIcon width={k(22)} height={k(22)} />}
								onPress={() => dispatch(facebookLoginRequest())}
								testId='cta-auth-facebook'
								disabled={busy}
								style={styles.facebook}
								labelStyle={styles.labelWhite}
							/>
							<SignInButton
								k={k}
								label={t('login.continue_with_google', 'Continue with Google')}
								icon={<GoogleIcon width={k(22)} height={k(22)} />}
								onPress={useGoogleWebButton ? undefined : () => dispatch(googleLoginRequest())}
								testId='cta-auth-google'
								disabled={busy}
								style={styles.google}
								labelStyle={styles.labelDark}
							>
								{useGoogleWebButton ? (
									<View
										id={googleButtonId}
										style={styles.googleWebHitArea}
										{...testProps('cta-auth-google')}
									/>
								) : null}
							</SignInButton>
							{showApple ? (
								<SignInButton
									k={k}
									label={t('login.continue_with_apple', 'Continue with Apple')}
									icon={<AppleIcon width={k(17.77)} height={k(22)} />}
									onPress={() => dispatch(appleLoginRequest())}
									testId='cta-auth-apple'
									disabled={busy}
									style={styles.apple}
									labelStyle={[styles.labelWhite, styles.labelShadowed]}
								/>
							) : null}
							<SignInButton
								k={k}
								label={t('login.continue_with_email', 'Continue with Email')}
								icon={<EmailIcon width={k(24.1)} height={k(18.86)} />}
								onPress={() => setStep('email')}
								disabled={busy}
								plate={{ stops: EMAIL_FILL, stroke: '#FFF908' }}
								style={styles.email}
								labelStyle={[styles.labelGold, styles.labelShadowed]}
							/>
						</View>
					) : (
						<View style={styles.stack}>
							<FormField
								k={k}
								value={data.username}
								placeholder={
									isRegistering ? t('T_SITE_REGISTER_EMAIL') : t('T_SITE_LOGIN_USERNAME')
								}
								error={errors.username}
								editable={!busy}
								onChangeText={value => onChange('username', value)}
								keyboardType={isRegistering ? 'email-address' : 'default'}
								returnKeyType='next'
								onSubmitEditing={() => passwordRef.current?.focus()}
							/>
							<FormField
								k={k}
								inputRef={passwordRef}
								value={data.password}
								placeholder={t('T_SITE_LOGIN_PASSWORD')}
								error={errors.password}
								editable={!busy}
								secureTextEntry
								onChangeText={value => onChange('password', value.replace(/\s/g, ''))}
								returnKeyType='go'
								onSubmitEditing={submit}
							/>
							<TouchableOpacity
								ref={submitRef}
								accessibilityRole='button'
								disabled={busy}
								activeOpacity={0.85}
								onPress={submit}
								style={[styles.submit, busy && styles.dimmed]}
								{...testProps(isRegistering ? 'cta-auth-register' : 'cta-auth-login')}
							>
								<GradientPlate
									width={stackWidth}
									height={k(STACK.buttonHeight)}
									radius={k(10)}
									stops={LOGIN_FILL}
									stroke='#B8FF8C'
									strokeWidth={1}
								/>
								<Text style={styles.submitLabel}>
									{(isRegistering
										? t('T_SITE_MENU_REGISTER', { defaultValue: 'Register' })
										: t('T_SITE_LOGIN_TITLE')
									).toUpperCase()}
								</Text>
							</TouchableOpacity>
							<View style={styles.formLinks}>
								<TouchableOpacity
									disabled={busy}
									hitSlop={8}
									onPress={() => {
										setErrors({});
										setStep('welcome');
									}}
								>
									<Text
										style={styles.link}
									>{`‹ ${t('login.other_options', 'Other ways to log in')}`}</Text>
								</TouchableOpacity>
								{isDev ? (
									<TouchableOpacity
										disabled={busy}
										hitSlop={8}
										onPress={() => setIsRegistering(value => !value)}
										{...testProps('cta-auth-toggle-mode')}
									>
										<Text style={styles.link}>
											{isRegistering
												? t('T_SITE_LOGIN_HAVE_ACCOUNT', {
														defaultValue: 'Have an account? Log in',
													})
												: t('T_SITE_MENU_REGISTER', { defaultValue: 'Register' })}
										</Text>
									</TouchableOpacity>
								) : null}
							</View>
							<InviteCodeField disabled={busy} inputStyle={styles.input} />
						</View>
					)}

					<View
						style={styles.languageRow}
						onLayout={event => setLanguageTop(event.nativeEvent.layout.y)}
					>
						<TouchableOpacity
							accessibilityRole='button'
							accessibilityState={{ expanded: languageOpen }}
							disabled={busy}
							activeOpacity={0.85}
							onPress={() => setLanguageOpen(open => !open)}
							style={styles.languageButton}
						>
							<LanguageFlag code={currentLanguage.code} k={k} />
							<Text style={styles.languageLabel}>{currentLanguage.label}</Text>
							{/* Closed it points up, towards the menu it opens; open it points down. */}
							<ChevronIcon
								width={k(11.75)}
								height={k(6.75)}
								style={languageOpen ? undefined : styles.chevronUp}
							/>
						</TouchableOpacity>
					</View>

					<Text style={styles.legal}>
						{t('login.recaptcha_notice', 'This site is protected by reCAPTCHA\nand the Google')}{' '}
						<Text style={styles.legalLink} onPress={() => Linking.openURL(RECAPTCHA_PRIVACY_URL)}>
							{t('login.privacy_policy', 'Privacy Policy')}
						</Text>{' '}
						{t('login.and', 'and')}{' '}
						<Text style={styles.legalLink} onPress={() => Linking.openURL(RECAPTCHA_TERMS_URL)}>
							{t('login.terms_of_service', 'Terms of Service')}
						</Text>{' '}
						{t('login.apply', 'apply')}
					</Text>
				</View>

				{languageOpen && languageTop != null ? (
					<>
						<Pressable
							sound={false}
							style={StyleSheet.absoluteFill}
							onPress={() => setLanguageOpen(false)}
						/>
						<LanguageMenu
							k={k}
							left={frameLeft + (frameWidth - k(LANGUAGE.menuWidth)) / 2}
							top={k(STACK.y) + languageTop - k(LANGUAGE.menuHeight + LANGUAGE.menuGap)}
							value={currentLanguage.code}
							onSelect={changeLanguage}
						/>
					</>
				) : null}
			</IndicatorScrollView>
		</ImageBackground>
	);
};

/** "Button / Facebook", "Google", "Apple" and "Email": 342 x 48, the icon 69 in, label 12 after. */
const SignInButton = ({
	k,
	label,
	icon,
	onPress,
	disabled,
	style,
	labelStyle,
	plate,
	children,
	testId,
}: {
	k: (value: number) => number;
	label: string;
	testId?: string;
	icon: React.ReactNode;
	onPress?: () => void;
	disabled?: boolean;
	style?: object;
	labelStyle?: object;
	plate?: { stops: GradientStops; stroke: string };
	children?: React.ReactNode;
}) => (
	<TouchableOpacity
		accessibilityRole='button'
		accessibilityLabel={label}
		activeOpacity={0.85}
		disabled={disabled || !onPress}
		onPress={onPress}
		{...testProps(testId)}
		style={[
			{
				height: k(STACK.buttonHeight),
				borderRadius: k(10),
				paddingLeft: k(STACK.iconX),
				gap: k(12),
				flexDirection: 'row',
				alignItems: 'center',
			},
			style,
			disabled && { opacity: 0.6 },
		]}
	>
		{plate ? (
			<GradientPlate
				width={k(STACK.width)}
				height={k(STACK.buttonHeight)}
				radius={k(10)}
				stops={plate.stops}
				stroke={plate.stroke}
				strokeWidth={1}
			/>
		) : null}
		<View style={{ width: k(24), height: k(24), alignItems: 'center', justifyContent: 'center' }}>
			{icon}
		</View>
		<Text numberOfLines={1} style={labelStyle}>
			{label}
		</Text>
		{children}
	</TouchableOpacity>
);

const FormField = ({
	k,
	error,
	inputRef,
	...inputProps
}: React.ComponentProps<typeof TextInput> & {
	k: (value: number) => number;
	error?: string;
	inputRef?: React.Ref<TextInput>;
}) => {
	const styles = fieldStyles(k);
	return (
		<View>
			<TextInput
				ref={inputRef}
				autoCapitalize='none'
				autoCorrect={false}
				placeholderTextColor='#A88E86'
				style={[styles.input, error ? styles.inputError : null]}
				{...inputProps}
			/>
			<Text style={styles.error}>{error ?? ' '}</Text>
		</View>
	);
};

/** "Flag / *" (20 x 14): EN and TR are the file's artwork, the striped ones are drawn. */
const LanguageFlag = ({ code, k }: { code: LanguageCode; k: (value: number) => number }) => {
	const size = { width: k(20), height: k(14) };
	if (code === 'en') return <FlagEn {...size} />;
	if (code === 'tr') return <FlagTr {...size} />;
	const stripes: Record<'bg' | 'fr' | 'ru', { colors: string[]; vertical?: boolean }> = {
		bg: { colors: ['#FFFFFF', '#00966E', '#D62612'] },
		ru: { colors: ['#FFFFFF', '#0039A6', '#D52B1E'] },
		fr: { colors: ['#002395', '#FFFFFF', '#ED2939'], vertical: true },
	};
	const flag = stripes[code];
	return (
		<View
			style={[
				size,
				{
					flexDirection: flag.vertical ? 'row' : 'column',
					borderRadius: k(2),
					overflow: 'hidden',
					borderWidth: 0.5,
					borderColor: 'rgba(0,0,0,0.25)',
				},
			]}
		>
			{flag.colors.map(color => (
				<View key={color} style={{ flex: 1, backgroundColor: color }} />
			))}
		</View>
	);
};

/** "Language Menu" (5948:29592): 232 wide, rows 44 high and 2 apart inside 6 of padding. */
const LanguageMenu = ({
	k,
	left,
	top,
	value,
	onSelect,
}: {
	k: (value: number) => number;
	left: number;
	top: number;
	value: LanguageCode;
	onSelect: (code: LanguageCode) => void;
}) => {
	const [size, setSize] = useState({ width: 0, height: 0 });
	const styles = menuStyles(k);
	return (
		<View
			onLayout={event => setSize(event.nativeEvent.layout)}
			style={[styles.menu, { left, top }]}
		>
			{size.width > 0 ? (
				<GradientPlate
					width={size.width}
					height={size.height}
					radius={k(14)}
					stops={[
						['0', '#520505'],
						['1', '#290000'],
					]}
					stroke='#CE7C00'
					strokeWidth={1}
				/>
			) : null}
			{LANGUAGES.map(language => {
				const selected = language.code === value;
				return (
					<TouchableOpacity
						key={language.code}
						accessibilityRole='menuitem'
						accessibilityState={{ selected }}
						activeOpacity={0.8}
						onPress={() => onSelect(language.code)}
						style={[styles.item, selected && styles.itemSelected]}
					>
						<LanguageFlag code={language.code} k={k} />
						<Text style={[styles.itemLabel, selected && styles.itemLabelSelected]}>
							{language.label}
						</Text>
						{selected ? <CheckIcon width={k(15.25)} height={k(11.75)} /> : null}
					</TouchableOpacity>
				);
			})}
		</View>
	);
};

const BottomFade = ({ top, width, height }: { top: number; width: number; height: number }) => {
	const fadeId = useSvgId('loginFade');
	return (
		<Svg
			pointerEvents='none'
			width={width}
			height={height}
			style={{ position: 'absolute', left: 0, top }}
		>
			<Defs>
				<LinearGradient id={fadeId} x1='0' y1='0' x2='0' y2='1'>
					<Stop offset='0' stopColor='#1A0000' stopOpacity={0} />
					<Stop offset='0.18' stopColor='#1A0000' stopOpacity={0.85} />
					<Stop offset='1' stopColor='#0F0000' stopOpacity={1} />
				</LinearGradient>
			</Defs>
			<Rect width={width} height={height} fill={`url(#${fadeId})`} />
		</Svg>
	);
};

const label = (k: (value: number) => number) => ({
	fontFamily: 'Sofia Sans',
	fontSize: k(16),
	lineHeight: k(20),
	fontWeight: '700' as const,
});

const createStyles = (k: (value: number) => number, frameLeft: number) =>
	StyleSheet.create({
		root: { ...StyleSheet.absoluteFillObject, backgroundColor: '#0F0000' },
		fill: { flex: 1 },
		hero: {
			position: 'absolute',
			left: frameLeft + k(HERO.x),
			top: k(HERO.y),
			width: k(HERO.width),
			height: k(HERO.height),
		},
		close: {
			position: 'absolute',
			left: frameLeft + k(CLOSE.x),
			top: k(CLOSE.y),
			width: k(CLOSE.size),
			height: k(CLOSE.size),
			alignItems: 'center',
			justifyContent: 'center',
			zIndex: 2,
		},
		lower: {
			marginTop: k(STACK.y),
			marginLeft: frameLeft + k(STACK.x),
			width: k(STACK.width),
		},
		stack: { gap: k(STACK.gap) },
		facebook: { backgroundColor: '#1877F2' },
		google: { backgroundColor: '#FFFFFF' },
		googleWebHitArea: {
			...StyleSheet.absoluteFillObject,
			justifyContent: 'center',
			alignItems: 'center',
			opacity: 0.01,
		},
		apple: { backgroundColor: '#000000', borderWidth: 1, borderColor: '#595959' },
		// Figma: #FFF908 rim with 0 0 9px rgba(64,255,51,0.45) around a #970501 - #220802 plate.
		email: {
			backgroundColor: '#5E0601',
			shadowColor: '#40FF33',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.45,
			shadowRadius: k(9),
			elevation: 4,
		},
		labelWhite: { ...label(k), color: '#FFFFFF' },
		labelDark: { ...label(k), color: '#1F1F1F' },
		labelGold: { ...label(k), color: '#FFF5B9', letterSpacing: k(0.32) },
		labelShadowed: {
			letterSpacing: k(0.32),
			textShadowColor: 'rgba(0,64,20,0.6)',
			textShadowOffset: { width: 0, height: 1 },
			textShadowRadius: 2,
		},
		submit: {
			height: k(STACK.buttonHeight),
			borderRadius: k(10),
			backgroundColor: '#248F1C',
			alignItems: 'center',
			justifyContent: 'center',
			shadowColor: '#40FF33',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.45,
			shadowRadius: k(9),
			elevation: 4,
		},
		submitLabel: {
			fontFamily: 'Sofia Sans',
			fontSize: k(18),
			lineHeight: k(20),
			fontWeight: '900',
			letterSpacing: k(0.36),
			color: '#F7FAF7',
			textShadowColor: 'rgba(0,64,20,0.6)',
			textShadowOffset: { width: 0, height: 1 },
			textShadowRadius: 2,
		},
		dimmed: { opacity: 0.6 },
		formLinks: { flexDirection: 'row', justifyContent: 'space-between' },
		link: {
			fontFamily: 'Sofia Sans',
			fontSize: k(14),
			lineHeight: k(20),
			fontWeight: '700',
			color: '#FFD166',
		},
		input: fieldStyles(k).input,
		languageRow: {
			marginTop: k(STACK_TO_LANGUAGE),
			height: k(LANGUAGE.height),
			alignItems: 'center',
		},
		// "Language Selector" (5947:77545): 36 high, rounded 18, #330E07 in a #CE7C00 rim.
		languageButton: {
			height: k(LANGUAGE.height),
			borderRadius: k(18),
			borderWidth: 1,
			borderColor: '#CE7C00',
			backgroundColor: '#330E07',
			paddingLeft: k(14),
			paddingRight: k(12),
			gap: k(8),
			flexDirection: 'row',
			alignItems: 'center',
			shadowColor: '#FF7300',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.35,
			shadowRadius: k(6),
		},
		languageLabel: {
			fontFamily: 'Sofia Sans',
			fontSize: k(14),
			lineHeight: k(20),
			fontWeight: '700',
			color: '#FFF5B9',
		},
		chevronUp: { transform: [{ rotate: '180deg' }] },
		// "Legal" (5947:77541): Medium 12 / 16, #A88E86 with #D9C4BC underlined links.
		legal: {
			marginTop: k(LANGUAGE_TO_LEGAL),
			fontFamily: 'Sofia Sans',
			fontSize: k(12),
			lineHeight: k(16),
			fontWeight: '500',
			color: '#A88E86',
			textAlign: 'center',
		},
		legalLink: { color: '#D9C4BC', textDecorationLine: 'underline' },
	});

const fieldStyles = (k: (value: number) => number) =>
	StyleSheet.create({
		input: {
			height: k(STACK.buttonHeight),
			borderRadius: k(10),
			borderWidth: 1,
			borderColor: '#CE7C00',
			backgroundColor: '#330E07',
			paddingHorizontal: k(16),
			fontFamily: 'Sofia Sans',
			fontSize: k(16),
			fontWeight: '600',
			color: '#FFF5B9',
		},
		inputError: { borderColor: '#FF5A4F' },
		error: {
			marginTop: k(2),
			minHeight: k(14),
			fontFamily: 'Sofia Sans',
			fontSize: k(12),
			lineHeight: k(14),
			fontWeight: '600',
			color: '#FF8A80',
		},
	});

const menuStyles = (k: (value: number) => number) =>
	StyleSheet.create({
		menu: {
			position: 'absolute',
			width: k(LANGUAGE.menuWidth),
			padding: k(6),
			gap: k(2),
			borderRadius: k(14),
			shadowColor: '#FF7200',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.6,
			shadowRadius: k(10),
			elevation: 8,
			backgroundColor: '#3A0303',
		},
		item: {
			height: k(44),
			borderRadius: k(10),
			paddingHorizontal: k(12),
			gap: k(10),
			flexDirection: 'row',
			alignItems: 'center',
			borderWidth: 1,
			borderColor: 'transparent',
		},
		itemSelected: { backgroundColor: '#6B1405', borderColor: '#C7781F' },
		itemLabel: {
			flex: 1,
			fontFamily: 'Sofia Sans',
			fontSize: k(16),
			lineHeight: k(20),
			fontWeight: '700',
			color: '#FFF5B9',
		},
		itemLabelSelected: { fontWeight: '900', color: '#FFFF99' },
	});

export default LoginWelcome;
